import { computed, inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { DriverscamVideoMinimumInfo, MinimumSlideInformation, QuestionIdentifier } from '@pcp/core/interfaces';
import { ActiveElementUiStateService, fromNavigationPreview } from 'shared/classroom/data-access';
import { SlidePreviewFacade } from '../slide-preview.facade';
import { NavigationPreviewStore, PREVIEW_MAX_VISIBLE_COUNT } from './navigation-preview.store';

/**
 * Facade of the beamer nav layouts (`pcp-next-slides-preview-glassmorphic`, and the classic
 * `pcp-next-slides-preview`): reads the nav state, and handles the outputs of the pure UI parts
 * (`shared/classroom/preview-screen/ui-beamer-nav`).
 *
 * Owns no state: the preview indices and toggles live in `NavigationPreviewStore`, the slide lists
 * in the NgRx store, the click intents go to `SlidePreviewFacade`.
 * Provided per layout component; `SlidePreviewFacade` comes from the parent `app-slide-preview`.
 */
@Injectable()
export class NextSlidesPreviewFacade {
    private readonly uiState = inject(ActiveElementUiStateService);
    private readonly navigationPreview = inject(NavigationPreviewStore);
    private readonly store = inject(Store);
    private readonly facade = inject(SlidePreviewFacade);

    readonly visibleCount = PREVIEW_MAX_VISIBLE_COUNT;

    /** The basic slides preview is open. */
    readonly showBasicSlidesPreview = this.navigationPreview.isShowingBasicSlidesPreview;
    /** The extension slides preview is open. */
    readonly showExtensionSlidesPreview = this.navigationPreview.showExtensionSlidesPreview;

    readonly basicSlides = this.store.selectSignal(fromNavigationPreview.selectBasicSlideList);
    /** Show basic slide with index (0...n-1) as preview */
    readonly basicSlideIdx = computed(() => this.navigationPreview.basicSlidePreviewIdx() ?? 0);
    /** Index of the basic slide which is currently activated. */
    private readonly currentBasicSlideIdxInStore = this.store.selectSignal(fromNavigationPreview.selectCurrentBasicSlideIdx);
    readonly currentBasicSlideIdx = computed(() => this.currentBasicSlideIdxInStore() ?? 0);

    private readonly extensionSlideList = this.store.selectSignal(fromNavigationPreview.selectExtensionSlides);
    readonly extensionSlides = computed(() => this.extensionSlideList().slides);
    /** Show extension slide with index (0...n-1) as first visible slide in preview */
    readonly extensionSlidePreviewIdx = this.navigationPreview.extensionSlidePreviewIdx;
    /**
     * 0-based index of the extension slide that is selected in the workspace (and shown on the beamer screen);
     * -1 when the current slide is not an extension slide, undefined without a current slide.
     */
    readonly currentExtensionSlideIndex = computed(() => {
        const slide = this.uiState.primaryLayerSignals.slide();
        return slide ? this.extensionSlides()?.findIndex(s => s.id === slide.id) : undefined;
    });
    readonly extensionPreviewNextEnabled = this.navigationPreview.extensionSlidePreviewNextEnabled;
    readonly extensionPreviewPreviousEnabled = this.navigationPreview.extensionSlidePreviewPreviousEnabled;

    /** If there is an assignment for the current slide -> the vimeo video ID for the DC video */
    readonly assignedDriverscamVideo = this.store.selectSignal(fromNavigationPreview.selectAssignedDriverscamVimeoVideoId);
    /** Difficult question numbers of the current active slide (tertiary, secondary, primary layer - whatever is shown). */
    readonly difficultQuestionNumbers = this.uiState.difficultQuestionNumbers;

    readonly hasExtensionSlides = computed(() => (this.extensionSlides()?.length ?? 0) > 0);
    readonly extensionSlidesNavigationVisible = computed(
        () => this.showExtensionSlidesPreview() && this.hasExtensionSlides(),
    );
    readonly expanded = computed(() => this.showBasicSlidesPreview() || this.extensionSlidesNavigationVisible());
    /** A driverscam or a difficult-question indicator is shown. */
    readonly hasActionIndicators = computed(
        () => !!this.assignedDriverscamVideo() || !!this.difficultQuestionNumbers()?.length,
    );

    // outputs of the parts → NavigationPreviewStore
    basicPrevious() {
        this.navigationPreview.showPreviewForPreviousBasicSlide();
    }

    basicNext() {
        this.navigationPreview.showPreviewForNextBasicSlide();
    }

    basicSwiped(index: number) {
        this.navigationPreview.setBasicSlidePreviewIdx(index);
    }

    basicToggled(show: boolean) {
        if (show) {
            this.navigationPreview.showBasicSlidesPreviewOnly();
        } else {
            this.navigationPreview.hideBasicSlidesPreview();
        }
    }

    extensionPrevious() {
        this.navigationPreview.showPreviewForPreviousExtensionSlide();
    }

    extensionNext() {
        this.navigationPreview.showPreviewForNextExtensionSlide();
    }

    extensionPreviewIdxChanged(index: number) {
        this.navigationPreview.setExtensionSlidePreviewIdx(index);
    }

    extensionToggled(show: boolean) {
        if (show) {
            this.navigationPreview.showPreviewForExtensionSlides();
        } else {
            this.navigationPreview.hidePreviewForExtensionSlides();
        }
    }

    // outputs of the parts → SlidePreviewFacade (beamer → teacher intents)
    slideClicked(slide: MinimumSlideInformation) {
        this.facade.onNextPreviewSlideClicked(slide);
    }

    driverscamAssignmentClicked(driverscam: DriverscamVideoMinimumInfo) {
        this.facade.onDriverscamAssignmentClicked(driverscam);
    }

    difficultQuestionIndicatorClicked(difficultQuestionNumbers: QuestionIdentifier[]) {
        this.facade.onDifficultQuestionIndicatorClicked(difficultQuestionNumbers);
    }
}
